import React from "react";

const Spec = () => {
  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-8">
        
        {/* Header */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">AI Qualification Spec</h2>
          <p className="text-gray-600">Specification for AI-powered candidate qualification and scoring.</p>
        </div>

        {/* PERSONA 1 */}
        <section className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-bold text-purple-600 mb-4">PERSONA 1: Sales Intern International Outbound</h3>
          
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Internship Type</h4>
              <p className="text-gray-900">Fin d'étude internship</p>
              <p className="text-gray-600 text-sm">6 months minimum</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Reporting Line</h4>
              <p className="text-gray-900">Works directly with the Co-Founder, Head of Sales</p>
            </div>
          </div>

          {/* Primary Mission */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Primary Mission</h4>
            <ul className="list-disc list-inside text-gray-900 space-y-1">
              <li>Support outbound sales operations and close first meetings using Walego</li>
              <li>Run demos, qualify leads, assist Elite client management</li>
            </ul>
          </div>

          {/* Education */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Education Background Required</h4>
            <ul className="list-disc list-inside text-gray-900 space-y-1">
              <li>Business school, Sales, International Business, Entrepreneurship, or Growth related degree</li>
              <li>Final year student</li>
            </ul>
          </div>

          {/* Languages */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Languages Required</h4>
            <div className="flex gap-2">
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">English fluent (mandatory)</span>
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">French professional level (strong plus)</span>
            </div>
          </div>

          {/* Geographic Scope */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Geographic Scope</h4>
            <p className="text-gray-900">International markets: Europe, US, Global B2B clients</p>
          </div>
        </section>

        {/* Hard Skills */}
        <section className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Hard Skills to Verify with AI</h3>
          <div className="space-y-3">
            {[
              "Understands B2B sales funnel from lead to close",
              "Can explain what a discovery call is",
              "Comfortable running demos on SaaS tools",
              "Strong understanding of LinkedIn as a sales channel",
              "Uses LinkedIn daily or weekly",
              "Has an active LinkedIn profile with real connections",
              "Knows or has used LinkedIn Sales Navigator",
              "Understands basic CRM logic",
              "Comfortable with Google Suite and Microsoft Suite",
            ].map((skill, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <span className="text-purple-500 mt-1">•</span>
                <span className="text-gray-900">{skill}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Sales Specific Knowledge */}
        <section className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Sales Specific Knowledge Signals</h3>
          <div className="space-y-3">
            {[
              "Can explain outbound vs inbound sales",
              "Knows what ICP and buyer persona mean",
              "Understands qualification concepts like BANT or MEDDICC at a basic level",
              "Understands cold outreach logic and follow-up sequences",
            ].map((signal, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <span className="text-blue-500 mt-1">•</span>
                <span className="text-gray-900">{signal}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Behavioral Signals */}
        <section className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Behavioral Signals to Validate</h3>
          <div className="space-y-3">
            {[
              "Comfortable speaking on video calls",
              "Not afraid to talk to decision makers",
              "Shows curiosity about AI and automation",
              "Result-driven mindset",
              "Asks smart questions about performance metrics",
            ].map((signal, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <span className="text-green-500 mt-1">•</span>
                <span className="text-gray-900">{signal}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Disqualification */}
        <section className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-bold text-red-600 mb-4">What Disqualifies Instantly</h3>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="space-y-2">
              {[
                "No LinkedIn activity or empty profile",
                "Cannot explain how LinkedIn can be used for sales",
                "No interest in outbound or prospecting",
                "Poor spoken English",
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">✕</span>
                  <span className="text-red-800">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Success Metrics */}
        <section className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Success Metrics During Internship</h3>
          <div className="grid grid-cols-2 gap-4">
            {[
              "Number of demos booked",
              "Demo to meeting conversion rate",
              "Ability to handle Elite accounts with autonomy",
              "Sales process understanding after 30 days",
            ].map((metric, idx) => (
              <div key={idx} className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <span className="text-emerald-800">{metric}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Location Bonus */}
        <section className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Current Location</h3>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800">
              <strong>Bonus:</strong> Students already based in Barcelona for their studies is a strong plus for the Walego hiring team 
              (we do not need to get them a NIE in that case, which speeds up the process).
            </p>
          </div>
        </section>

        {/* AI Scoring Prompt */}
        <section className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">AI Scoring Prompt</h3>
          <div className="bg-gray-900 rounded-lg p-4 overflow-auto">
            <pre className="text-green-400 text-sm whitespace-pre-wrap font-mono">
{`You are evaluating candidates for a Sales Intern International Outbound position at Walego.
This is a 6-month minimum end-of-studies internship focused on supporting outbound sales operations.

## SCORING CRITERIA (Total: 100 points)

### 1. EDUCATION & BACKGROUND (15 points)
- Business school, Sales, International Business, Entrepreneurship, or Growth-related degree: +15 pts
- Final year student (mandatory): Required
- Other relevant business/commercial degree: +10 pts

### 2. LANGUAGE PROFICIENCY (20 points)
- English fluent (mandatory): +15 pts (DISQUALIFY if not fluent)
- French professional level: +5 pts bonus
- Poor spoken English: DISQUALIFY

### 3. LINKEDIN & SALES PRESENCE (20 points)
- Active LinkedIn profile with real connections: +10 pts
- Uses LinkedIn daily/weekly: +5 pts
- Experience with LinkedIn Sales Navigator: +5 pts
- No LinkedIn activity or empty profile: DISQUALIFY

### 4. B2B SALES KNOWLEDGE (25 points)
- Understands B2B sales funnel (lead to close): +5 pts
- Can explain discovery calls: +5 pts
- Knows outbound vs inbound sales difference: +5 pts
- Understands ICP and buyer persona concepts: +5 pts
- Familiar with qualification frameworks (BANT, MEDDICC): +5 pts

### 5. TECHNICAL & TOOL SKILLS (10 points)
- Comfortable running demos on SaaS tools: +4 pts
- Understands basic CRM logic: +3 pts
- Proficient with Google Suite and Microsoft Suite: +3 pts

### 6. BEHAVIORAL SIGNALS (10 points)
- Comfortable speaking on video calls: +3 pts
- Not afraid to talk to decision makers: +3 pts
- Shows curiosity about AI and automation: +2 pts
- Result-driven mindset: +2 pts

## INSTANT DISQUALIFICATION
- No LinkedIn activity or empty profile
- Cannot explain how LinkedIn can be used for sales
- No interest in outbound or prospecting
- Poor spoken English
- Not a final year student

## BONUS POINTS (up to +10 extra)
- Already based in Barcelona: +10 pts
- Previous sales/SDR internship experience: +5 pts
- Experience with cold outreach sequences: +5 pts

## SCORING THRESHOLDS
- QUALIFIED (auto-advance): >= 70 points
- REVIEW (manual decision): 50-69 points
- DISQUALIFIED (auto-reject): < 50 points OR instant disqualification triggered

## OUTPUT FORMAT
{
  "score": <0-100>,
  "qualified": true/false,
  "disqualified_reason": "<reason if disqualified, null otherwise>",
  "strengths": ["<strength 1>", "<strength 2>"],
  "gaps": ["<gap 1>", "<gap 2>"],
  "summary": "<1-2 sentence evaluation summary>",
  "linkedin_assessment": "<assessment of their LinkedIn presence>",
  "sales_readiness": "<assessment of their B2B sales understanding>"
}`}
            </pre>
          </div>
        </section>

      </div>
    </div>
  );
};

export default Spec;
